import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";
import { ChevronRightIcon, HomeIcon } from "lucide-react";
import * as React from "react";

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

type Segment = {
  readonly depth: number;
  readonly label: string;
  readonly to: string | undefined;
};

type BreadcrumbActions = {
  readonly add: (id: string, segment: Segment) => void;
  readonly remove: (id: string) => void;
};

const BreadcrumbActionsContext = React.createContext<BreadcrumbActions | null>(null);
const BreadcrumbSegmentsContext = React.createContext<ReadonlyMap<string, Segment>>(new Map());
const BreadcrumbDepthContext = React.createContext<number>(0);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

const BreadcrumbProvider = ({ children }: { readonly children: React.ReactNode; }) => {
  const [segments, setSegments] = React.useState<Map<string, Segment>>(
    () => new Map(),
  );

  const actions = React.useMemo<BreadcrumbActions>(
    () => ({
      add: (id, segment) =>
        setSegments((prev) => {
          const next = new Map(prev);
          next.set(id, segment);
          return next;
        }),
      remove: (id) =>
        setSegments((prev) => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        }),
    }),
    [],
  );

  return (
    <BreadcrumbActionsContext.Provider value={actions}>
      <BreadcrumbSegmentsContext.Provider value={segments}>
        {children}
      </BreadcrumbSegmentsContext.Provider>
    </BreadcrumbActionsContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------

const BreadcrumbBar = ({ className, ...props }: React.ComponentProps<"nav">) => {
  const segments = React.useContext(BreadcrumbSegmentsContext);

  const sorted = Array.from(segments.values()).sort(
    (a, b) => a.depth - b.depth,
  );

  if (sorted.length === 0) return null;

  return (
    <nav
      data-slot="breadcrumb-bar"
      aria-label="Breadcrumb"
      className={cn(
        "flex w-0 flex-1 items-center gap-1 overflow-hidden text-sm text-muted-foreground",
        className,
      )}
      {...props}
    >
      <Link to="/" className="hover:text-foreground transition-colors">
        <HomeIcon className="size-3.5" />
      </Link>

      {sorted.map((segment, i) => {
        const isLast = i === sorted.length - 1;
        return (
          <React.Fragment key={`${segment.depth}-${segment.label}`}>
            <ChevronRightIcon className="size-3 shrink-0 text-muted-foreground/60" />
            {isLast || !segment.to
              ? (
                <span
                  data-slot="breadcrumb-leaf"
                  className={cn(
                    "min-w-0 truncate",
                    isLast
                      ? "shrink-0 text-foreground font-medium"
                      : "shrink text-muted-foreground",
                  )}
                >
                  {segment.label}
                </span>
              )
              : (
                <Link
                  data-slot="breadcrumb-link"
                  to={segment.to}
                  className="min-w-0 shrink truncate hover:text-foreground transition-colors"
                >
                  {segment.label}
                </Link>
              )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Breadcrumb wrapper
// ---------------------------------------------------------------------------

type BreadcrumbProps = {
  readonly label: string;
  readonly to?: string | undefined;
  readonly children: React.ReactNode;
};

const BreadcrumbRoot = ({ label, to, children }: BreadcrumbProps) => {
  const actions = React.useContext(BreadcrumbActionsContext);
  const depth = React.useContext(BreadcrumbDepthContext);
  const idRef = React.useRef(crypto.randomUUID());

  React.useLayoutEffect(() => {
    if (!actions) return;
    const id = idRef.current;
    actions.add(id, { depth, label, to });
    return () => actions.remove(id);
  }, [actions, depth, label, to]);

  return (
    <BreadcrumbDepthContext.Provider value={depth + 1}>
      {children}
    </BreadcrumbDepthContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Trail (convenience for multiple segments)
// ---------------------------------------------------------------------------

type TrailSegment = {
  readonly label: string;
  readonly to?: string | undefined;
};

type TrailProps = {
  readonly segments: ReadonlyArray<TrailSegment>;
  readonly children: React.ReactNode;
};

const BreadcrumbTrail = ({ segments, children }: TrailProps) => {
  return segments.reduceRight<React.ReactNode>(
    (acc, seg) => (
      <BreadcrumbRoot label={seg.label} to={seg.to}>
        {acc}
      </BreadcrumbRoot>
    ),
    children,
  );
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const Breadcrumb = Object.assign(BreadcrumbRoot, {
  Provider: BreadcrumbProvider,
  Bar: BreadcrumbBar,
  Trail: BreadcrumbTrail,
});
